import { useState } from 'react'
import type { FormEvent } from 'react'

import { AthenaError } from '../lib/api/client.ts'
import { useAuth } from '../lib/auth/AuthContext.tsx'
import { Button } from '../components/primitives/Button.tsx'
import { TextField } from '../components/primitives/Field.tsx'
import { ThemeToggle } from '../components/primitives/ThemeToggle.tsx'

/**
 * A recusa é sempre a mesma frase.
 *
 * O servidor devolve `invalid_credentials` para usuário inexistente, senha
 * errada, conta desativada e conta bloqueada — e a interface **não tenta
 * adivinhar qual foi**. Distinguir daria a quem sonda um oráculo para enumerar
 * operadores e para descobrir qual conta vale a pena atacar. "Sua conta está
 * bloqueada" é exatamente a frase que não se pode escrever aqui.
 */
const REFUSAL = 'Usuário ou senha inválidos.'

export function Login() {
  const { signIn, endedReason } = useAuth()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault()
    if (busy) return

    setBusy(true)
    setError(null)

    signIn(username.trim(), password)
      .catch((cause: unknown) => {
        if (cause instanceof AthenaError && cause.status === 401) {
          setError(REFUSAL)
          return
        }
        // Rede, proxy fora, 500: aí sim vale dizer o que houve, porque não é
        // sobre a credencial e o operador precisa saber que o problema é outro.
        setError(
          cause instanceof AthenaError
            ? `Falha no servidor (${cause.status}). Tente de novo em instantes.`
            : 'Não foi possível falar com o servidor.',
        )
      })
      .finally(() => setBusy(false))
  }

  return (
    <div className="flex min-h-screen flex-col bg-canvas">
      <div className="flex items-center gap-3 border-b border-line px-3 py-1.5">
        <span className="font-mono text-base font-medium tracking-label text-fg">HERMES</span>
        <span className="text-micro uppercase tracking-label text-fg-muted">
          segurança de endpoints
        </span>
        <div className="ml-auto">
          <ThemeToggle />
        </div>
      </div>

      <div className="flex flex-1 items-center justify-center px-4 py-10">
        <form
          onSubmit={submit}
          className="flex w-full max-w-sm flex-col gap-3 border border-line bg-surface p-4"
        >
          <div>
            <h1 className="text-base font-medium text-fg">Entrar</h1>
          </div>

          <TextField
            label="Usuário"
            value={username}
            onValueChange={setUsername}
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            required
            mono
            autoFocus
          />

          <TextField
            label="Senha"
            type="password"
            value={password}
            onValueChange={setPassword}
            autoComplete="current-password"
            required
          />

          {error === null && endedReason !== null && (
            <p role="status" className="border-l-2 border-warn px-2 py-1 text-sm leading-snug text-warn">
              {endedReason}
            </p>
          )}

          {error !== null && (
            <p role="alert" className="border-l-2 border-crit px-2 py-1 text-sm leading-snug text-crit">
              {error}
            </p>
          )}

          <Button type="submit" variant="primary" busy={busy} className="w-full">
            {busy ? 'Entrando…' : 'Entrar'}
          </Button>
        </form>
      </div>
    </div>
  )
}
